import { useEffect, useMemo, useState, useRef } from 'react';
import { BaseMessageProps, MessagesByPosition } from '@/shared/types/message';
import { messageConfig } from '../config/message.config';
import { messageService } from '../services/MessageService';

export const useMessage = () => {
  const [messages, setMessages] = useState<BaseMessageProps[]>([]);
  const timersRef = useRef<Map<number, NodeJS.Timeout>>(new Map());
  const handlerRef = useRef<((messageData: BaseMessageProps) => void) | null>(null);

  useEffect(() => {
    const listeners = messageService.getListeners();

    // Evitar registrar múltiples handlers
    if (handlerRef.current) {
      const existingIndex = listeners.indexOf(handlerRef.current);
      if (existingIndex > -1) {
        listeners.splice(existingIndex, 1);
      }
    }

    const handler = (messageData: BaseMessageProps) => {
      if (messageData.isLeaving) {
        setMessages((prev) =>
          prev.map((m) => (m.id === messageData.id ? { ...m, isLeaving: true } : m)),
        );

        // Limpiar el timer si existe
        const timer = timersRef.current.get(messageData.id);
        if (timer) {
          clearTimeout(timer);
          timersRef.current.delete(messageData.id);
        }

        // Eliminar después de completar la animación
        const removeTimer = setTimeout(() => {
          setMessages((prev) => prev.filter((m) => m.id !== messageData.id));
        }, messageConfig.animationDuration);

        // Guardar el timer de eliminación también
        timersRef.current.set(messageData.id, removeTimer);
        return;
      }

      setMessages((prev) => {
        const exists = prev.some((m) => m.id === messageData.id);
        if (exists) return prev;

        // Asegurar que el nuevo mensaje tenga isLeaving: false para la animación de entrada
        const newMessage = { ...messageData, isLeaving: false };
        const newMessages = [...prev, newMessage];

        // Programar cierre automático si tiene duración y es mayor a 0
        const duration =
          newMessage.duration !== undefined ? newMessage.duration : messageConfig.duration;

        // Validar duration antes de usar
        const validDuration = duration > 0 && isFinite(duration) && !isNaN(duration) ? duration : 0;

        if (validDuration > 0) {
          const timer = setTimeout(() => {
            messageService.dismiss(newMessage.id);
          }, validDuration);
          timersRef.current.set(newMessage.id, timer);
        }

        return newMessages.slice(-messageConfig.maxMessages);
      });
    };

    handlerRef.current = handler;
    listeners.push(handler);

    return () => {
      const index = listeners.indexOf(handler);
      if (index > -1) listeners.splice(index, 1);

      // Limpiar todos los timers activos (copiar ref para el cleanup)
      // eslint-disable-next-line react-hooks/exhaustive-deps -- copia de ref en cleanup es intencional
      const timers = timersRef.current;
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
      handlerRef.current = null;
    };
  }, []);

  // Agrupar mensajes por posición
  const messagesByPosition = useMemo(
    () =>
      messages.reduce((acc, messageItem) => {
        // Validar posición
        const validPositions = [
          'top-left',
          'top-center',
          'top-right',
          'center-left',
          'center',
          'center-right',
          'bottom-left',
          'bottom-center',
          'bottom-right',
        ];
        const position =
          messageItem.position && validPositions.includes(messageItem.position)
            ? messageItem.position
            : 'top-right';

        if (!acc[position]) {
          acc[position] = [];
        }
        acc[position]!.push(messageItem);
        return acc;
      }, {} as MessagesByPosition),
    [messages],
  );

  return { messagesByPosition, messages };
};
